import React from "react";

export type OrderStatus = "pending" | "processing" | "shipped" | "delivered" | string;

interface OrderStatusTimelineProps {
  currentStatus?: OrderStatus;
  t: (key: string) => string;
  getStatusText: (status: string) => string;
}

const STATUSES: OrderStatus[] = ["pending", "processing", "shipped", "delivered"];

const OrderStatusTimeline: React.FC<OrderStatusTimelineProps> = ({
  currentStatus = "pending",
  t,
  getStatusText,
}) => {
  const currentIndex = STATUSES.indexOf(currentStatus);
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;

  const progressWidth = `${(safeIndex / (STATUSES.length - 1)) * 100}%`;

  return (
    <div className="mb-8">
      <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center gap-2">
        <span className="text-blue-500">📊</span>
        {t("order.orderProgress")}
      </h3>

      <div className="relative">
        {/* Background bar */}
        <div className="absolute top-4 left-0 right-0 h-2 bg-gray-200 rounded-full" />

        {/* Progress bar */}
        <div
          className="absolute top-4 left-0 h-2 bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
          style={{ width: progressWidth }}
        />

        <div className="relative flex justify-between">
          {STATUSES.map((status, index) => {
            const isCompleted = index <= safeIndex;
            const isCurrent = status === currentStatus;

            return (
              <div key={status} className="flex flex-col items-center z-10">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-300 ${
                    isCompleted
                      ? "bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-lg scale-110"
                      : "bg-white border-2 border-gray-300 text-gray-400"
                  }`}
                  aria-label={t(`orderStatus.${status}`)}
                >
                  {isCompleted ? <span className="text-sm">✓</span> : <span className="text-sm">{index + 1}</span>}
                </div>

                <span
                  className={`text-sm font-medium capitalize ${
                    isCompleted ? "text-gray-800" : "text-gray-500"
                  }`}
                >
                  {getStatusText(status)}
                </span>

                {isCurrent && (
                  <span className="text-xs text-blue-600 font-medium mt-1">
                    {t("order.current")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrderStatusTimeline;